import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useState } from "react";
import { Button, Card, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import api from "../../config/api";
import { getDate, LocalFormatDate, parseAmount, rupeeFormat } from "../../config/functions";
// import ReportGrandTotalBar from "./ReportGrandTotalBar";
import {
    ClientRegistrationType,
    fetchClientRegistrationAction,
} from "../../redux/actions/clientRegistration";
import { AppDispatch, StoreState } from "../../redux/store";
import GstSalesReportFilter from "./GstSalesReportFilter";

interface InvoiceData {
    financialYear: string;
    date: string;
    day: string;
    invoiceNo: string;
    customerName: string;
    gstNumber: string;
    place: string;
    rate: number;
    taxable: number;
    total: number;
    cessAmount: number;
    invoiceType: string;
    eCommerceGstIn: number | string;
    revCharge: number;
    appTaxRate: number;
}

const GstSalesInvoiceWise = () => {
    const dispatch = useDispatch<AppDispatch>();

    const [fromDate, setFromDate] = useState<string>("");
    const [toDate, setToDate] = useState<string>("");
    const [branchId, setBranchId] = useState<string>("");
    const [customerType, setCustomerType] = useState<string>("");
    const [tableData, setTableData] = useState<InvoiceData[]>([]);
    const [triggerSearch, setTriggerSearch] = useState<boolean>(false);
    const [hasSearched, setHasSearched] = useState(false);

    const clients = useSelector<StoreState, ClientRegistrationType[]>(
        (state) => state.clientRegistration
    );

    useEffect(() => {
        dispatch(fetchClientRegistrationAction());
    }, []);

    useEffect(() => {
        if (triggerSearch) {
            api()
              .get(
                `reports/gstSalesInvoiceWise/?fromDate=${getDate(fromDate)}&toDate=${getDate(
                  toDate,
                )}&branchId=${branchId}&customerType=${customerType}`,
              )
              .then((res) => {
                if (res.status === 200 && res.data.data) {
                  setTableData(res.data.data);
                }
              })
              .catch((error) => {
                console.error("Error fetching sales data:", error);
              });

            setTriggerSearch(false);
        }
    }, [triggerSearch, fromDate, toDate, branchId, customerType]);

    
    const [filters, setFilters] = useState<any>({});

    const handleSearch = (newFilters: any) => {
        setFilters(newFilters);
        setFromDate(newFilters.fromDate);
        setToDate(newFilters.toDate);
        setBranchId(newFilters.branchId);
        setCustomerType(newFilters.customerType);
        setHasSearched(true);
        setTriggerSearch(true);
    };

    const uniqueGstinCount = new Set(
        (tableData || [])
            .map((r) => (r.gstNumber || "").trim())
            .filter((gst) => gst.length > 0)
    ).size;

    const totalInvValue = (tableData || []).reduce(
        (sum, r) => sum + parseAmount(r.total),
        0,
    );
    const totalTaxableValue = (tableData || []).reduce((sum, r) => sum + (Number(r.taxable) || 0), 0);

    const toRow = (item: InvoiceData, index: number) => [
        String(index + 1),
        item.gstNumber ?? "",
        item.customerName ?? "",
        item.invoiceNo ?? "",
        LocalFormatDate(item.date) ?? "",
        rupeeFormat(item.total) ?? "0",
        item.place ?? "",
        String(item.revCharge ?? 0),
        String(item.appTaxRate ?? 0),
        item.invoiceType ?? "",
        // String(item.eCommerceGstIn ?? ""),
        String(item.rate ?? 0),
        rupeeFormat(item.taxable) ?? "0",
        // rupeeFormat(item.cessAmount) ?? "0",
    ];

    const downloadCSV = () => {
        const headerLabels = [
          "SI.No",
          "GSTIN Recipient",
          "Receiver Name",
          "Inv.No",
          "Inv.Date",
          "Inv.Value",
          "Place of Supply",
          "Rev.Charge",
          "App% Tax Rate",
          "Invoice Type",
          // "E-Commerce GSTIN",
          "Rate",
          "Taxable Value",
          // "Cess Amount",
        ];

        const rows = tableData?.map((item, idx) => toRow(item, idx)) || [];

        rows.push([
            "", // SI.No
            `${uniqueGstinCount}`, // GSTIN Recipient column
            "", // Receiver Name
            "", // Inv.No
            "", // Inv.Date
            rupeeFormat(totalInvValue) ?? "0", // Inv.Value
            "", // Place of Supply
            "", // Rev.Charge
            "", // App% Tax Rate
            "", // Invoice Type
            // "", // E-Commerce GSTIN
            "", // Rate
            rupeeFormat(totalTaxableValue) ?? "0", // Taxable Value
            // "", // Cess Amount
        ]);

        const csvCell = (value: unknown) => {
            const s = value === null || value === undefined ? "" : String(value);
            return `"${s.replace(/"/g, '""')}"`;
        };

        const csvContent = [
            headerLabels.map(csvCell).join(","),
            ...rows.map((row) => row.map(csvCell).join(",")),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `TAP_sales_${customerType} _${LocalFormatDate(getDate(filters?.fromDate)) || ""}_${LocalFormatDate(getDate(filters?.toDate)) || ""}`;
        link.click();
    };

    const downloadPDF = () => {
        const logoUrl = clients[0]?.shopPhoto;

        const doc = new jsPDF("landscape");
        const marginX = 10;
        const boxWidth = doc.internal.pageSize.width - 2 * marginX;
        const boxHeight = 25;
        let startY = 15;

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");

        doc.rect(marginX, startY - 5, boxWidth, boxHeight);

        if (logoUrl) {
            doc.addImage(logoUrl, "JPEG", marginX + 5, startY - 2, 25, 20);
        }

        const paddingBetweenHeaderAndTable = 3;
        const textX = marginX + 35;

        doc.text(clients[0]?.shopFullName || "", textX, startY);
        doc.setFont("helvetica", "normal");
        doc.text(
            `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}, ${clients[0]?.district || ""
            }`,
            textX,
            startY + 5
        );
        doc.text(
            `${clients[0]?.state || ""} - ${clients[0]?.pincode || ""}, ${clients[0]?.contactNo1 || ""
            }`,
            textX,
            startY + 10
        );
        doc.text(`GSTIN: ${clients[0]?.gstNo || ""}`, textX, startY + 15);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        const heading = `GST ${customerType} Sales invoice wise report`;
        const headingWidth = doc.getTextWidth(heading);
        const pageWidth = doc.internal.pageSize.width;
        doc.text(heading, marginX + boxWidth - headingWidth - 10, startY + 8);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const financialYearText = `Financial Year: ${tableData[0]?.financialYear || ""
            }`;
        const headingStartX = marginX + boxWidth - headingWidth - 10;
        doc.text(financialYearText, headingStartX, startY + 13);

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        const fromDate =
            "From : " + (LocalFormatDate(getDate(filters?.fromDate)) || "");
        const toDate = "To : " + (LocalFormatDate(getDate(filters?.toDate)) || "");

        const tableStartY = startY + boxHeight + paddingBetweenHeaderAndTable;
        doc.text(fromDate, marginX, tableStartY);
        doc.text(toDate, marginX + 50, tableStartY);

        autoTable(doc, {
          startY: 47,
          showFoot: "lastPage",
          head: [
            [
              "SI.No",
              "GSTIN Recipient",
              "Receiver Name",
              "Inv.No",
              "Inv.Date",
              "Inv.Value",
              "Place of Supply",
              "Rev.Charge",
              "App% Tax Rate",
              "Invoice Type",
              // "E-Commerce GSTIN",
              "Rate",
              "Taxable Value",
              // "Cess Amount",
            ],
          ],
          body: (tableData || []).map((item, idx) => toRow(item, idx)),
          foot: [
            [
              "",
              `${uniqueGstinCount}`,
              "",
              "",
              "",
              rupeeFormat(totalInvValue) ?? "0",
              "",
              "",
              "",
              "",
              // "",
              "",
              rupeeFormat(totalTaxableValue) ?? "0",
              // "",
            ],
          ],
          theme: "grid",
          styles: { fontSize: 8, textColor: 0, lineColor: 0, lineWidth: 0.1 },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: 0,
            fontStyle: "bold",
            lineColor: 0,
            lineWidth: 0.1,
          },
          footStyles: {
            fillColor: [255, 255, 255],
            halign: "right",
          },
          margin: { top: 25, left: 10, right: 10 },
          columnStyles: {
            0: { halign: "center" }, // SI.No
            1: { halign: "left" }, // GSTIN Recipient
            2: { halign: "left" }, // Receiver Name
            3: { halign: "left" }, // Inv.No
            4: { halign: "center" }, // Inv.Date
            5: { halign: "right" }, // Inv.Value
            6: { halign: "left" }, // Place of Supply
            7: { halign: "right" }, // Rev.Charge
            8: { halign: "right" }, // App% Tax Rate
            9: { halign: "left" }, // Invoice Type
            // 10: { halign: "left" }, // E-Commerce GSTIN
            10: { halign: "right" }, // Rate
            11: { halign: "right" }, // Taxable Value
            // 13: { halign: "right" }, // Cess Amount
          },
        });

        // Footer Section (Page Number)
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            let pageHeight = doc.internal.pageSize.height;
            let footerY = pageHeight - 11;

            doc.setFontSize(10);
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, footerY);
        }

        doc.save(
          `TAP_sales_${customerType} _${LocalFormatDate(getDate(filters?.fromDate)) || ""}_${LocalFormatDate(getDate(filters?.toDate)) || ""}.pdf`,
        );
    };
    // tableData.map((item, index) => {
    //     console.log(item.quantity);
    // });

    return (
      <>
        <GstSalesReportFilter onSearch={handleSearch} />
        {/* <ReportGrandTotalBar show={hasSearched} amount={totalInvValue} /> */}
        {hasSearched && tableData.length > 0 ? (
          <Card className="mt-2 p-2 shadow-lg border-0 rounded-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="fw-bold">Sales invoice details</h5>
              <div>
                <Button
                  className="me-2 btn-sm"
                  variant="success"
                  onClick={downloadCSV}
                >
                  Download CSV
                </Button>
                <Button
                  className="me-2 btn-sm"
                  variant="success"
                  onClick={downloadPDF}
                >
                  Download PDF
                </Button>
              </div>
            </div>
            <div>Total : {(tableData || []).reduce(
        (sum, r) => sum + parseAmount(r.total),
        0,
    )}</div>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>SI.No</th>
                  <th>GSTIN Recipient</th>
                  <th>Receiver Name</th>
                  <th>Inv.No</th>
                  <th>Inv.Date</th>
                  <th>Inv.Value</th>
                  <th>Place of Supply </th>
                  <th>Rev.Charge</th>
                  <th>App% Tax Rate</th>
                  <th>Invoice Type</th>
                  {/* <th>E-Commerce GSTIN</th> */}
                  <th>Rate</th>
                  <th>Taxable Value</th>
                  {/* <th>Cess Amount </th> */}
                </tr>
              </thead>

              <tbody>
                {tableData?.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.gstNumber}</td>
                    <td>{item.customerName}</td>
                    <td>{item.invoiceNo}</td>
                    <td>{LocalFormatDate(item.date)}</td>
                    <td>{rupeeFormat(item.total)}</td>
                    <td>{item.place}</td>
                    <td>{item.revCharge}</td>
                    <td>{item.appTaxRate}</td>
                    <td>{item.invoiceType}</td>
                    {/* <td>{item.eCommerceGstIn}</td> */}
                    <td>{item.rate}</td>
                    <td>{rupeeFormat(item.taxable)}</td>
                    {/* <td>{rupeeFormat(item.cessAmount)}</td> */}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        ) : hasSearched ? (
          <h4 className="text-center fw-bold text-secondary">
            No such data found!
          </h4>
        ) : null}
      </>
    );
};

export default GstSalesInvoiceWise;